/**
 * AssistantLauncher — the always-visible way into an AI assistant.
 *
 * Shared and vertical-agnostic: it takes its artwork, its label and its
 * first-visit line from props, so a hotel or a property surface can open its
 * own assistant with the same component.
 *
 * RULES BUILT IN, not left to a page: it is never icon-only, never inside a
 * menu, never collapsed and never hideable by the customer; the touch target
 * is 56px (64px from 768px up); the idle motion is slow, stops entirely under
 * prefers-reduced-motion and is pointer-events:none so it can never eat a tap;
 * and the first-visit bubble closes itself after 8 seconds and is remembered
 * server-side so it never comes back.
 *
 * IT ALSO STEPS ASIDE. Any element marked `data-launcher-avoid="lift"` is a
 * PRIMARY action (Add to list, Send, a confirm) and the launcher lifts itself
 * just above the nearest one. The lift is measured from the UNLIFTED box, so it
 * cannot oscillate; it is capped at MAX_LIFT so the chef never flies up the
 * screen; and it never leaves the viewport. It stays fully solid and fully
 * tappable throughout — it is never removed, never ghosted, never icon-only and
 * never collapsed, because a launcher you cannot press is worse than one that
 * is in the way. Secondary controls (favourite chips and the like) are NOT
 * lifted away from: pages reserve a bottom gutter so the last row always
 * clears the hat, and content is allowed to scroll behind a floating button.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

export default function AssistantLauncher({
    icon, label, hint, badge, onOpen, showBubble = false, onBubbleSeen,
    testId = 'assistant-launcher', hidden = false, freeze = false,
}) {
    const { t } = useTranslation();
    const [bubble, setBubble] = useState(showBubble);
    const seen = useRef(onBubbleSeen);
    seen.current = onBubbleSeen;

    useEffect(() => { setBubble(showBubble); }, [showBubble]);
    useEffect(() => {
        if (!bubble) return undefined;
        const id = setTimeout(() => { setBubble(false); seen.current?.(); }, 8000);
        return () => clearTimeout(id);
    }, [bubble]);

    const dismiss = () => { setBubble(false); seen.current?.(); };

    // A FORM IS NOT A PLACE FOR A FLOATING BUTTON, AND NEITHER IS AN OPEN
    // SHEET. While a field is focused (so while the keyboard is up) or any
    // full-screen grocery sheet is open, the launcher steps out of the way
    // entirely — which is why it can no longer sit over "Add a user", an aisle
    // tile, a delivery window or "New question".
    const [typing, setTyping] = useState(false);
    const [sheet, setSheet] = useState(false);
    useEffect(() => {
        const focused = () => {
            const el = document.activeElement;
            const tag = (el?.tagName || '').toLowerCase();
            return tag === 'input' || tag === 'textarea' || tag === 'select'
                || el?.getAttribute?.('contenteditable') === 'true';
        };
        const sync = () => {
            setTyping(focused());
            setSheet(!!document.querySelector('[data-grocery-sheet]'));
        };
        document.addEventListener('focusin', sync);
        document.addEventListener('focusout', sync);
        const mo = new MutationObserver(sync);
        mo.observe(document.body, { childList: true, subtree: true });
        sync();
        return () => {
            document.removeEventListener('focusin', sync);
            document.removeEventListener('focusout', sync);
            mo.disconnect();
        };
    }, []);

    const wrapRef = useRef(null);
    const [lift, setLift] = useState(0);
    const liftRef = useRef(0);
    liftRef.current = lift;
    const MAX_LIFT = 160;

    const measure = useCallback(() => {
        const el = wrapRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const base = { top: r.top + liftRef.current, bottom: r.bottom + liftRef.current,
            left: r.left, right: r.right };
        const boxes = (sel) => Array.from(document.querySelectorAll(sel))
            .map((n) => n.getBoundingClientRect())
            .filter((b) => b.width > 0 && b.height > 0 && b.bottom > 0 && b.top < window.innerHeight);
        const hits = (b, top, bottom) => b.left < base.right && b.right > base.left
            && b.top < bottom && b.bottom > top;

        const primary = boxes('[data-launcher-avoid="lift"]');
        let need = 0;
        for (let pass = 0; pass < 3; pass += 1) {
            let grew = 0;
            primary.forEach((b) => {
                if (hits(b, base.top - need, base.bottom - need)) {
                    grew = Math.max(grew, base.bottom - need - b.top + 12);
                }
            });
            if (!grew) break;
            need += grew;
        }
        const room = Math.max(0, base.top - 72);
        const next = Math.round(Math.min(need, MAX_LIFT, room));
        if (next !== liftRef.current) setLift(next);
    }, []);

    useEffect(() => {
        if (hidden || typing || sheet) return undefined;
        // A guided tour anchors its own bubble and ring to this button, so while
        // a tour is running the launcher holds still and hands the collision
        // rules back to the tour.
        if (freeze) { setLift(0); return undefined; }
        let frame = 0;
        const schedule = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => { frame = 0; measure(); });
        };
        schedule();
        window.addEventListener('scroll', schedule, true);
        window.addEventListener('resize', schedule);
        const tick = setInterval(schedule, 400);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            clearInterval(tick);
            window.removeEventListener('scroll', schedule, true);
            window.removeEventListener('resize', schedule);
        };
    }, [hidden, measure, freeze, bubble, typing, sheet]);

    // A FLOATING BUTTON MUST BE ABLE TO STEP ASIDE. While a panel is open that
    // the customer is meant to READ, the launcher hides itself rather than
    // sitting on the words. Opt-in, default off, so no other surface changes.
    if (hidden || typing || sheet) return null;

    return (
        <div ref={wrapRef}
            style={{ transform: lift ? `translateY(-${lift}px)` : undefined }}
            className="pointer-events-none fixed bottom-24 right-3 z-[70] flex flex-col items-end gap-2 transition-transform duration-200 md:right-6"
            data-testid={`${testId}-wrap`} data-lift={lift}>
            {bubble ? (
                <div className="chef-bubble pointer-events-auto relative max-w-[15rem] rounded-2xl rounded-br-sm border border-slate-200 bg-white px-3 py-2 pr-9 text-sm font-medium text-slate-800 shadow-xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                    data-testid={`${testId}-bubble`}>
                    {hint}
                    <button onClick={dismiss} aria-label={t('askChef.close')}
                        data-testid={`${testId}-bubble-close`}
                        className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ) : null}
            {badge ? (
                <span className="pointer-events-auto rounded-full bg-slate-900/85 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                    data-testid={`${testId}-badge`}>{badge}</span>
            ) : null}
            <button onClick={() => { dismiss(); onOpen(); }} data-testid={testId}
                className="pointer-events-auto flex h-[60px] w-[60px] shrink-0 flex-col items-center justify-center gap-0.5 rounded-2xl border-2 border-[#13265C] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.28)] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 dark:border-white/80 dark:bg-slate-900 md:h-16 md:w-16">
                <span className="relative flex h-7 w-7 shrink-0 items-center justify-center md:h-8 md:w-8">
                    <span aria-hidden="true"
                        className="chef-steam pointer-events-none absolute -top-1 right-0 h-2.5 w-1 rounded-full bg-slate-400/70" />
                    <img src={icon} alt="" aria-hidden="true"
                        className="chef-hat-idle pointer-events-none h-7 w-7 md:h-8 md:w-8" />
                </span>
                {/* NEVER ICON-ONLY: the word is part of the button, at every width. */}
                <span className="px-0.5 text-[11px] font-extrabold leading-none text-[#13265C] dark:text-white"
                    data-testid={`${testId}-label`}>
                    {label}
                </span>
            </button>
        </div>
    );
}
